import '../consts/events.quiz-folder';

import { logger } from '@zep/core-logger';
import { trackEvent } from '@zep/logger/tracking';

import { FOLDER_ERROR, readApiStatus } from '../consts/errors';
import type {
  FolderSaveContext,
  FolderSavePlace,
} from '../consts/events.quiz-folder';
import type { SaveQuizTarget } from '../types/saveQuizLocation.types';

/**
 * 서버가 정상적으로 거절한 경우. 사용자 입력이나 상태에 따른 것이라 장애가 아니다 —
 * Sentry 로 보내면 과금되고 실제 장애가 묻힌다.
 */
const EXPECTED_REJECTIONS = new Set<string>([
  ...Object.values(FOLDER_ERROR),
  'EXPIRED_CURSOR',
  'INVALID_CURSOR',
]);

/** 이벤트의 `reason` 값. 서버 코드가 있으면 그 값을, 없으면 실패 종류를 쓴다. */
export const toTrackingFailReason = (error: unknown): string => {
  const status = readApiStatus(error);
  if (status) return status;
  if (error instanceof Error && /network|timeout/i.test(error.message)) {
    return 'network';
  }
  return 'unknown';
};

/**
 * 폴더 흐름의 실패를 기록한다. 예상한 거절은 디버그 로그로, 그 밖은 에러로 남긴다.
 * 화면 안내(토스트·알럿)는 호출부가 하던 대로 한다.
 */
export const reportFolderError = (
  error: unknown,
  context: { action: string; folderId?: string; quizSetId?: string },
) => {
  const status = readApiStatus(error);
  const extra = {
    status: status ?? 'unknown',
    folder_id: context.folderId ?? '',
    quiz_set_id: context.quizSetId ?? '',
  };

  // 기록이 흐름을 깨면 안 된다. 기록 실패는 삼킨다.
  try {
    if (status && EXPECTED_REJECTIONS.has(status)) {
      logger.debug(`quiz-folder ${context.action} rejected`, extra);
      return;
    }
    logger.captureError(error, {
      tags: { feature: 'quiz-folder', action: context.action },
      extra,
    });
  } catch {
    // no-op
  }
};

export const toFolderSaveContext = (
  quiz: Pick<SaveQuizTarget, 'id' | 'intent' | 'place' | 'isQuizOwner'>,
): FolderSaveContext => ({
  place: quiz.place,
  intent: quiz.intent,
  quiz_set_id: quiz.id,
  is_quiz_owner: quiz.isQuizOwner,
});

/**
 * 비로그인 사용자를 로그인으로 보내기 직전의 `entry_click`.
 *
 * 로그인 경로는 `useShowSaveToFolderModal` 이 한 곳에서 찍고, 게스트 경로만 진입점마다 흩어져 있어 여기로 모았다.
 * 게스트는 남의 퀴즈만 저장하므로 `is_quiz_owner` 는 항상 false 다.
 */
export const trackSaveEntryAuthRequired = (
  place: FolderSavePlace,
  quizSetId: string,
) =>
  trackEvent('quiz_folder_save_entry_click', {
    place,
    intent: 'copy',
    quiz_set_id: quizSetId,
    is_quiz_owner: false,
    auth_required: true,
  });
